// ==============================|| MATTER TYPES ||============================== //

import { ReactNode } from 'react';
import { BusinessAreaDto, ContactMinDto, PartyType } from './contacts';
import { ContractListItemMinDto, ServiceAgreementListItemDto } from './contracts';
import { CurrencyData } from './currency';
import { CustomField, CustomFieldDto } from './customFields';
import { DocumentTemplateMinDto } from './document';
import { CustomizationDto, InvoiceInfoDto } from './invoices';
import { OfficeMin } from './offices';
import {
    FinancialDataDto,
    MinDto,
    NamedEntityMinDto,
    NamedEntityMinWithEmailDto,
    NamedEntityMinWithMainEmailDto,
    NumericIdPrimKeyDto,
    OfficialyNamedEntityMinDto,
} from './other';
import { PracticeArea } from './practice-areas';
import { JobRoleDto } from './jobRoles';
import {
    HourlyRate,
    MatterFollowedPricelist,
    MatterPricelistWriteDto,
    PersonalRate,
    PersonalRateInput,
    Pricelist,
    PricingKind,
} from './pricelists';
import { ProjectMinDto } from './projects';
import { ReferralListItem } from './referrals';
import { TeamMinDto } from './teams';
import { DayOfWeek } from './tenants';
import { TimecardSummarizedStats } from './timecards';

export enum MatterStateType {
    COMPLETED = 'COMPLETED',
    OFFER_LOST = 'OFFER_LOST',
    OFFER_PENDING = 'OFFER_PENDING',
    OFFER_WON = 'OFFER_WON',
    IN_PROGRESS = 'IN_PROGRESS',
    ARCHIVED = 'ARCHIVED',
}

export enum MatterBlockingPolicyType {
    CLIENT_DEFAULT = 'CLIENT_DEFAULT',
}

export enum OfferValueType {
    CASH = 'CASH',
    HOURS = 'HOURS',
}

export enum EngagementLengthUnit {
    DAYS = 'DAYS',
    WEEKS = 'WEEKS',
    MONTHS = 'MONTHS',
}

export type MatterBlockingEffectiveStatus = 'BLOCKED' | 'IN_GRACE' | 'UNBLOCKED';

export type MatterBlockingReasonCode =
    | 'AML_GRACE'
    | 'PARTY_AML_BLOCK'
    | 'PARTY_UNIVERSAL_BLOCK'
    | 'MATTER_OVERRIDE_BLOCKED'
    | 'MATTER_OVERRIDE_UNBLOCKED'
    | 'NONE';

export interface MatterBlockingStatusDto {
    effectiveStatus: MatterBlockingEffectiveStatus;
    reasonCode: MatterBlockingReasonCode;
    graceUntil: string | null;
    blockedActionTypeCode: 'CASES' | 'TIMECARDS' | 'AML_TIMECARDS' | null;
}

export enum MatterVisibility {
    LIMITED = 'LIMITED',
}

export interface MatterVisibilityInfo {
    visibility?: MatterVisibility | 'FULL' | 'LIMITED';
    isConfidential?: boolean | null;
}

export interface MatterDeleteValidation {
    hasTimecards: boolean;
    hasExpenses: boolean;
    hasInvoices: boolean;
    hasInvoiceTimecards: boolean;
    hasInvoiceExpenses: boolean;
    hasInvoiceRows: boolean;
    draftTimecardAuthors: string[];
    hasClientAssets?: boolean;
}

export enum RemunerationCalcType {
    EQUALS_BILLED_TIME = 'EQUALS_BILLED_TIME',
    EQUALS_USED_TIME = 'EQUALS_USED_TIME',
    MANUAL_ENTRY = 'MANUAL_ENTRY',
}

export enum PricingLimitationType {
    HOURLY_CAPPED = 'HOURLY_CAPPED',
    FIXED_ONETIME = 'FIXED_ONETIME',
    FIXED_RECURRING = 'FIXED_RECURRING',
    HOURLY_UNLIMITED = 'HOURLY_UNLIMITED',
    SUCCESS_FEE = 'SUCCESS_FEE',
    NON_BILLABLE = 'NON_BILLABLE',
    PRO_BONO = 'PRO_BONO',
}

export enum MatterPartyRoleType {
    CLIENT_REPRESENTED = 'CLIENT_REPRESENTED',
    OPPONENT = 'OPPONENT',
    TARGET = 'TARGET',
    OTHER_PARTY = 'OTHER_PARTY',
    JUDGE = 'JUDGE',
    SECONDARY_RECIPIENT = 'SECONDARY_RECIPIENT',
}

export enum MatterEmployeeRoleType {
    STAFF = 'STAFF',
    PAYMENT_NOTIFICATION_RECEIVER = 'PAYMENT_NOTIFICATION_RECEIVER',
}

export interface ClientParty extends ContactMinDto {
    partyType: PartyType;
    isPersonaNonGrata: boolean;
    isInternalClient?: boolean;
}

export interface MatterListItem extends MatterListItemMin, MatterVisibilityInfo {
    matterStateTypeCode: string;
    openedDate: string;
    responsibleEmployee?: NamedEntityMinDto;
    supervisedByEmployee: NamedEntityMinDto;
    handledByOffice: OfficeMin;
    invoicedByOfficeId: number;
    payerPartyId: number;
    matterStats?: MatterStats;
    currencyDetails?: CurrencyData;
    referrals: ReferralListItem[] | null;
    visibility?: MatterVisibility | 'FULL' | 'LIMITED';
    isConfidential?: boolean | null;
    dmsLink?: string;
    clientMatterSequence?: number | null;
    isClientMatterSequenceEditable?: boolean | null;
    serviceAgreementContract: ServiceAgreementListItemDto | null;
    latestExpenseDate?: string | null;
    invoicingIntervalTypeCode?: string | null;
    nextInvoicingDate?: string | null;
    capUnitTypeCode?: string | null;
    capLimit?: number | null;
    fixedFee?: number | null;
    lastInvoice?: LastInvoiceDto | null;
    /** A case saved as a draft: listed only with `includeDrafts`, and only to its creator and those it is shared with. */
    isDraft?: boolean;
    /** The covers in force on the case's two leads, at most one per role; null on a LIMITED row. */
    activeCovers?: ActiveCoverDto[] | null;
}

/** A substitute covering one of a case's leads during the lead's absence. */
export interface ActiveCoverDto extends SubstituteAssigneeDto {
    role: 'SUPERVISING_PARTNER' | 'RESPONSIBLE_LAWYER';
    /** Null when the current user may not open the delegation. */
    delegationId: number | null;
}

/** A case's most recent invoice, as the case and billable-clients lists show it. */
export interface LastInvoiceDto {
    id: number;
    invoiceNumber: string;
    invoiceStatusTypeCode: string;
    invoiceDate: string;
}

export interface MatterListExportItem {
    id: number;
    name: string;
    caseNumber: string;
    fullName: string;
    clientParty: ClientParty;
    timecardLanguageCode: string;
    pricelistCurrencyTypeCode: string;
    pricelist?: Pricelist;
    matterStateTypeCode: string;
    openedDate: string;
    closedDate: string;
    responsibleEmployee?: NamedEntityMinDto;
    supervisedByEmployee?: NamedEntityMinDto;
    handledByOffice?: OfficeMin;
    invoicedByOffice: OfficeMin;
    payerParty: NamedEntityMinWithMainEmailDto | null;
    currencyDetails?: CurrencyData;
    description: string;
    employees?: {
        employee: NamedEntityMinDto;
        matterEmployeeRoleTypeCode: string;
        administrator?: boolean;
    }[];
    serviceAgreementContract: ContractListItemMinDto | null;
    isConfidential: boolean;
    lastTimecardDate: string | null;
    lastInvoiceInfo: InvoiceInfoDto | null;
    timecardSummarizedStats: TimecardSummarizedStats | null;
    partyBusinessAreas: BusinessAreaDto[] | null;
    practiceAreas: { id: number; name: string }[];
    dmsPracticeArea: { id: number; name: string } | null;
    secondLevelPracticeAreas: { id: number; name: string }[];
    referrals: ReferralListItem[] | null;
    clientReferrals: ReferralListItem[] | null;
}

export interface MatterListItemMin extends MatterVisibilityInfo {
    id: number;
    matterStateTypeCode: string;
    name: string;
    description: string;
    caseNumber: string;
    fullName: string;
    clientParty: ClientParty;
    timecardLanguageCode: string;
    pricelistCurrencyTypeCode: string;
    // Absent on a read means ROLE.
    pricingKind?: PricingKind;
    preserveNonBillableHours: boolean;
    isCurrentlyBlocked: boolean;
    pricingLimitationTypeCode: string;
    isNonBillable: boolean;
    remunerationCalcTypeCode: RemunerationCalcType;
    timecardIncrement?: number | null;
    project?: ProjectMinDto;
    isTimecardEntryPreviousMonthBlocked: boolean;
    timecardEntryDeadlineWithinCurrentMonth: number;
    isTimecardEntryPreviousWeekBlocked: boolean;
    timecardEntryDeadlineWithinCurrentWeek: DayOfWeek;
    visibility?: MatterVisibility | 'FULL' | 'LIMITED';
    isConfidential?: boolean | null;
    employees?: {
        employee: NamedEntityMinDto;
        matterEmployeeRoleTypeCode: string;
        administrator?: boolean;
    }[];
    responsibleEmployee?: NamedEntityMinDto;
    supervisedByEmployee?: NamedEntityMinDto;
    rates?: HourlyRate[];
    personalRates?: PersonalRate[];
}

export interface MatterStateCountDto {
    matterGroups: {
        groupName: 'connected' | 'opponent' | 'primaryContact' | 'judge' | 'other' | 'payer';
        stateCount: Map<string, number>;
    }[];
}

export interface SubstituteAssigneeDto {
    employee: MinDto;
    startDate: string;
    endDate: string;
}

export interface ConnectedCaseMinDto {
    id: number;
    name: string;
    matterStateTypeCode: string;
}

/** The Case a Timecard/Expense transfer moves items to — the Case just opened from an Offer Won Case. */
export type TransferDestinationCase = ConnectedCaseMinDto;

/** A payer of a case's invoices, with its share of the net amount in percent; the first of the list is the main payer. */
export interface MatterPayerDto {
    party: ContactMinDto;
    shareRate: number;
}

/** A payer as a save sends it; the list's order is the priority, its first entry the case's payer. */
export interface MatterPayerWriteDto {
    partyId: number;
    shareRate: number;
}

/** A payer row of the case form; the share is the input's text, a dot-decimal percent. */
export interface MatterPayerForm {
    party: ContactMinDto | null;
    shareRate: string;
}

export interface MatterDto {
    createdAt: Date;
    lastModifiedAt?: Date;
    id: number;
    name: string;
    caseNumber: string;
    fullName: string;
    matterStateTypeCode: string;
    matterTypeCode?: string;
    reasonOfferLostCode?: string;
    outcomeNotes?: string;
    offerValueTypeCode: string | null;
    offerValueLow: string | null;
    offerValueHigh: string | null;
    estimatedEngagementLength: string | null;
    estimatedEngagementUnitCode: string | null;
    // The Offer Won case this case was opened from.
    originatesFromMatter: ConnectedCaseMinDto | null;
    // The case that was opened from this Offer Won case.
    connectedCase: ConnectedCaseMinDto | null;
    clientParty: ClientParty;
    openedDate: string;
    closedDate?: string;
    responsibleEmployee?: NamedEntityMinDto;
    supervisedByEmployee?: NamedEntityMinDto;
    substituteSupervisingPartner?: SubstituteAssigneeDto;
    substituteResponsibleLawyer?: SubstituteAssigneeDto;
    delegationHandoverNotes?: string;
    /** The active delegation's return notes, else those of the latest delegation that left any. */
    delegationReturnNotes?: string;
    /** When the latest of those notes written after its cover's return was added; absent while a delegation is ACTIVE. */
    delegationReturnNotesAddedAt?: string | null;
    /** True while a non-terminal delegation exists on the case — the window the handover-notes field is shown. */
    hasNonTerminalDelegation?: boolean;
    /** True while a delegation on the case is ACTIVE — handover turns read-only and return notes follow the active cover. */
    hasActiveDelegation?: boolean;
    referredByEmployee?: NamedEntityMinDto;
    referrals?: ReferralListItem[];
    createdByEmployee?: MinDto;
    changedByEmployee?: MinDto;
    employees?: {
        employee: NamedEntityMinDto;
        matterEmployeeRoleTypeCode: string;
        administrator?: boolean;
    }[];
    teams?: TeamMinDto[];
    handledByOffice?: OfficeMin;
    primaryContactParty?: OfficialyNamedEntityMinDto;
    isNoOpponent?: boolean;
    parties?: {
        party: OfficialyNamedEntityMinDto;
        matterPartyRoleTypeCode: string;
    }[];
    rates?: HourlyRate[];
    personalRates?: PersonalRate[];
    generalNotes?: string;
    officialReference?: string;
    isConfidential?: boolean;
    confidentialReason?: string | null;
    confidentialReasonRecordedBy?: MinDto | null;
    confidentialReasonRecordedAt?: string | null;
    isConfidentialReasonRequiredOnClose: boolean;
    description: string;
    insiderInfo?: string | null;
    isInsider?: boolean | null;
    practiceAreas?: PracticeArea[];
    dmsPracticeArea: PracticeArea | null;
    serviceAgreementContract?: ContractListItemMinDto;
    timecardIncrement?: number | null;
    archivedDate?: string;
    archivedInOffice?: NamedEntityMinDto;
    archivingNotes?: string;
    archivingPropertiesTypeCode?: string;
    discountPct?: string;
    discountExpiryDate?: Date;
    isCostIncluded?: boolean;
    pricingLimitationTypeCode: string;
    accountingRevenueTypeCode?: string;
    capUnitTypeCode?: string;
    capLimit?: string;
    fixedFee?: string;
    recurringFixedFee?: string;
    billingNotes?: string;
    invoicingInfo: string | null;
    timecardLanguageCode: string;
    primaryRecipientParty?: NamedEntityMinWithMainEmailDto;
    primaryRecipientAdditionalEmails?: string;
    secondaryRecipientAdditionalEmails?: string;
    matterClientReference?: string;
    invoiceMatterName?: string;
    invoiceMatterNameLocalLanguage?: string;
    invoiceTemplate?: DocumentTemplateMinDto;
    invoicedByOffice?: NamedEntityMinDto;
    invoicingIntervalTypeCode?: string;
    payerParty: ContactMinDto;
    /** Empty when the case has no payer list of its own; `payerParty` is then its single payer. */
    payers: MatterPayerDto[];
    invoiceSpecTemplate?: DocumentTemplateMinDto;
    pricelist?: Pricelist;
    pricelistCurrencyTypeCode: string;
    paymentCurrencyTypeCode?: string;
    pricingHash?: string;
    // Absent on a read means ROLE.
    pricingKind?: PricingKind;
    followsPricelist?: MatterFollowedPricelist | null;
    currencyDetails?: CurrencyData;
    currencyAccessible: boolean;
    hasConnectedInvoices: boolean;
    /** Billable Timecards or Expenses that are still uninvoiced — the condition the COMPLETED block uses. */
    hasUninvoicedBillableWork: boolean;
    isDeletable: boolean;
    isEditable: boolean;
    /** A case saved as a draft: it is edited, completed and deleted through the drafts API, never as a case. */
    isDraft?: boolean;
    /** The client's case the backend creates when a contact becomes a client; none of its fields is mandatory. */
    isAdministrativeMatter?: boolean;
    allowedStatusTransitions?: string[];
    statusTransitionBlockers?: Record<string, string>;
    isJointInvoiceDisabled: boolean;
    isAutomaticClosingExempt: boolean;
    matterBlockingPolicyTypeCode: string;
    isAmlBlockingExempt: boolean;
    isCurrentlyBlocked: boolean;
    isAmlApplicable: boolean;
    blockingStatus: MatterBlockingStatusDto | null;
    isExcludedFromXR: boolean;
    customFields?: CustomField[];
    nextInvoicingDate?: Date;
    paymentNotificationReceivers?: {
        employee: NamedEntityMinDto;
        matterEmployeeRoleTypeCode: string;
    }[];
    dmsLink: string | null;
    dmsFolderId: string | null;
    isNonBillable: boolean;
    isAccessibleFirmWide: boolean;
    isInternal: boolean;
    remunerationCalcTypeCode: RemunerationCalcType;
    preserveNonBillableHours: boolean;
    project?: ProjectMinDto;
    isTimecardEntryPreviousMonthBlocked: boolean;
    timecardEntryDeadlineWithinCurrentMonth: number;
    isTimecardEntryPreviousWeekBlocked: boolean;
    timecardEntryDeadlineWithinCurrentWeek: DayOfWeek;
    financialData?: FinancialDataDto;
    isPaperFile: boolean;
    invoiceCustomization?: CustomizationDto;
    isSpecIncludedInInvoice: boolean;
    isSpecExcludedFromEmail: boolean;
    isRestricted?: boolean;
    restrictedAccessMessage?: string;
    blockingHistory?: string;
}

export interface MatterBillingSummaryDto {
    feeArrangementTypeCode: string;
    budget?: MatterBillingSummaryBudgetDto;
    fixedFeeDetails?: MatterBillingSummaryFixedFeeDetailsDto;
    recurringFeeDetails?: MatterBillingSummaryRecurringFeeDetailsDto;
}

interface MatterBillingSummaryBudgetDto {
    capTotal: number | null;
    usedCap: number | null;
    remainingCap: number | null;
    remainingInvoicedOnly?: number | null;
}

interface MatterBillingSummaryFixedFeeDetailsDto {
    fixedFee: number | null;
    billedAmount: number | null;
}

interface MatterBillingSummaryRecurringFeeDetailsDto {
    recurringFixedFee: number | null;
    invoicingIntervalTypeCode: string | null;
}

export interface UpdateHourlyRatesDto {
    referencedPricelist: PricelistReference;
    rates: HourlyRate[];
}

// PUT /v1/matters/{id}/pricelist: the referenced template plus one list's rows. A null pricelistId is the
// base list; an absent set of rows leaves those rows untouched, an empty one removes them.
export interface UpdateMatterPricelistDto extends UpdateHourlyRatesDto {
    pricelistId: number | null;
    personalRates?: PersonalRateInput[] | null;
}

export interface PricelistReference {
    id: number | null;
    pricingHash?: string;
}

// A personal row while it is edited: the person as shown, the role they hold today and the price.
export interface PersonalRateDraft {
    employee: NamedEntityMinDto;
    jobRole: JobRoleDto | null;
    hourlyPrice: string | null;
    // A following case's own price for the person, kept in every list whatever the followed list does.
    isException?: boolean;
}

// One tab of the case's price-list editor, and one staged list of the case form. A null id is a list the
// server has not seen: one the user added, or any list of a case being created or copied.
export interface PricelistTabDraft {
    key: string;
    // The kind of the case the tab prices; an employee-priced case's dated lists may start before it opens.
    pricingKind?: PricingKind;
    id: number | null;
    isBase: boolean;
    validFrom: string | null;
    name: string | null;
    rates: HourlyRate[];
    personalRates: PersonalRateDraft[];
}

export interface MatterStats {
    totalTimeInSeconds?: number;
    totalBlledTimeInSeconds?: number;
    totalTimeInHours?: number;
    totalBilledTimeInHours?: number;
    totalTimecardCount?: number;
    latestTimecardDate?: string;
    expenseList: Record<string, number>[];
    uninvoicedTimeInSeconds?: number;
    uninvoicedBilledTimeInSeconds?: number;
    uninvoicedTimeInHours?: number;
    uninvoicedBilledTimeInHours?: number;
    uninvoicedTimecardCount?: number;
    containsMissingJobRole: boolean;
    containsMissingHourlyPrice: boolean;
}

export interface MatterForm {
    readOnlyCaseNumber: string | null;
    name: string;
    clientParty: ClientParty | null;
    matterStateType: { label: string; value: string } | null;
    matterType: { label: string; value: string } | null;
    reasonOfferLost?: { label: string; value: string } | null;
    outcomeNotes?: string | null;
    offerValueType: { label: string; value: string } | null;
    offerValueLow: string | null;
    offerValueHigh: string | null;
    estimatedEngagementLength: string | null;
    estimatedEngagementUnit: { label: string; value: string } | null;
    // Only honoured on create; the link is cleared through the dedicated unlink action, never through an update.
    originatesFromMatterId: number | null;
    handledByOffice: OfficeMin | null;
    generalNotes: string | null;
    officialReference: string | null;
    isConfidential: boolean;
    confidentialReason?: string | null;
    description: string | null;
    /** Kept, and sent, while `isConfidential` is unchecked; only its field is hidden. */
    insiderInfo?: string | null;
    isInsider?: boolean;
    practiceAreas: PracticeArea[];
    secondLevelPracticeAreas: PracticeArea[];
    dmsPracticeArea: PracticeArea | null;
    serviceAgreementContract: ContractListItemMinDto | null;
    timecardIncrement: { label: string; value: number } | null;
    project: ProjectMinDto | null;
    openedDate: string | null;
    closedDate: string | null;
    responsibleEmployee: NamedEntityMinDto | null;
    supervisedByEmployee: NamedEntityMinDto | null;
    employees: NamedEntityMinDto[];
    administrators: NamedEntityMinDto[];
    primaryContactParty: NamedEntityMinDto | null;
    representedClients: NamedEntityMinDto[];
    isNoOpponent: boolean;
    opponentParties: NamedEntityMinDto[];
    targetParties: NamedEntityMinDto[];
    otherParties: NamedEntityMinDto[];
    judgeParties: NamedEntityMinDto[];
    archivedInOffice: NamedEntityMinDto | null;
    archivedDate: string | null;
    archivingNotes: string | null;
    archivingPropertiesType: { label: string; value: string } | null;
    discountPct: string | null;
    discountExpiryDate: string | null;
    isCostIncluded: boolean;
    pricingLimitationType: { label: string; value: string } | null;
    accountingRevenueType: { label: string; value: string } | null;
    capUnitType: { label: string; value: string } | null;
    capLimit: string | null;
    fixedFee: string | null;
    recurringFixedFee: string | null;
    billingNotes: string | null;
    invoicingInfo: string | null;
    timecardLanguage: { label: string; value: string } | null;
    primaryRecipientParty: NamedEntityMinWithEmailDto | null;
    primaryRecipientAdditionalEmails: string | null;
    secondaryRecipientParties: NamedEntityMinWithEmailDto[];
    secondaryRecipientAdditionalEmails: string | null;
    matterClientReference: string | null;
    invoiceMatterName: string | null;
    invoiceMatterNameLocalLanguage: string | null;
    invoiceTemplate: NamedEntityMinDto | null;
    invoicedByOffice: NamedEntityMinDto | null;
    invoicingIntervalType: { label: string; value: string } | null;
    /** The primary payer; kept equal to the primary row of `payers` while the shared payment feature is on. */
    payerParty: ContactMinDto | null;
    payers: MatterPayerForm[];
    invoiceSpecTemplate: NamedEntityMinDto | null;
    referencedPricelist: PricelistReference | null;
    // Every price list of the case as the form stages it, the base tab first; the save sends them all.
    pricelists: PricelistTabDraft[];
    // Fixed for the form: a case changes kind only through its pricing editor on the case page. A copy or a case
    // opened from a won offer carries its source's kind, which the create sends; absent on any other new case, which
    // takes the kind of its service agreement's list, else the tenant's mode.
    pricingKind?: PricingKind;
    // The list the case follows; its tabs are then a read-only copy that the save does not send.
    followsPricelist?: MatterFollowedPricelist | null;
    // The case a copy is made from, which the create names so the copy keeps that case's kind.
    copiedFromMatterId?: number | null;
    // The tabs are the unedited own copy of the list the case followed until this edit, which the save leaves to the
    // server's copy; see StagedPricelists.
    keepsServerCopy?: boolean;
    pricelistCurrencyType: { label: string; value: string } | null;
    isPaymentCurrencyDifferent: boolean;
    paymentCurrencyType: { label: string; value: string } | null;
    isJointInvoicingDisabled: boolean;
    isAutomaticClosingExempt: boolean;
    matterBlockingPolicyType: { label: string; value: string } | null;
    nextInvoicingDate: Date | null;
    paymentNotificationReceivers: NamedEntityMinDto[];
    dmsLink: string | null;
    isAccessibleFirmWide: boolean;
    isUsedTimeUsedForRemuneration: boolean;
    isExcludedFromXR: boolean;
    preserveNonBillableHours: boolean;
    isPaperFile: boolean;
    invoiceCustomization?: CustomizationDto | null;
    isSpecIncludedInInvoice: boolean;
    isSpecExcludedFromEmail: boolean;
    isAmlBlockingExempt: boolean;
    changeReasons: Record<string, string> | null;
}

/**
 * Router state read by the Case add page: `navigate('/matter/new', { state: { prefill } })` opens the form
 * prefilled from the given Offer Won Case and links the new Case back to it on save.
 */
export interface MatterAddPrefill {
    originatesFromMatterId: number;
}

export interface CreateEntityFieldChangeReasonDto {
    fieldName: string;
    oldValue: string | null;
    newValue: string | null;
    changeReasonText: string;
}

export interface MatterCreateDto {
    name: string;
    clientPartyId: number | null;
    matterStateTypeCode: string | null;
    matterTypeCode: string | null;
    reasonOfferLostCode: string | null;
    outcomeNotes: string | null;
    offerValueTypeCode: string | null;
    offerValueLow: string | null;
    offerValueHigh: string | null;
    estimatedEngagementLength: string | null;
    estimatedEngagementUnitCode: string | null;
    originatesFromMatterId: number | null;
    handledByOfficeId: number | null;
    generalNotes: string | null;
    officialReference: string | null;
    isConfidential: boolean;
    confidentialReason?: string | null;
    description: string | null;
    insiderInfo: string | null;
    isInsider: boolean;
    practiceAreas: NamedEntityMinDto[];
    dmsPracticeAreaId: number | null;
    serviceAgreementContractId: number | null;
    timecardIncrement: number | null;
    projects: NumericIdPrimKeyDto[] | null;
    openedDate: string | null;
    closedDate: string | null;
    responsibleEmployeeId: number | null;
    supervisedByEmployeeId: number | null;
    primaryContactPartyId: number | null;
    employees: NamedEntityMinDto[];
    administrators: NamedEntityMinDto[];
    representedClients: NamedEntityMinDto[];
    isNoOpponent: boolean;
    opponentParties: NamedEntityMinDto[];
    targetParties: NamedEntityMinDto[];
    otherParties: NamedEntityMinDto[];
    judgeParties: NamedEntityMinDto[];
    archivedDate: string | null;
    archivedInOfficeId: number | null;
    archivingNotes: string | null;
    archivingPropertiesTypeCode: string | null;
    discountPct: string | null;
    discountExpiryDate: Date | null;
    isCostIncluded: boolean;
    pricingLimitationTypeCode: string | null;
    accountingRevenueTypeCode: string | null;
    capUnitTypeCode: string | null;
    capLimit: string | null;
    fixedFee: string | null;
    recurringFixedFee: string | null;
    billingNotes: string | null;
    invoicingInfo: string | null;
    timecardLanguageCode: string | null;
    primaryRecipientPartyId: number | null;
    primaryRecipientAdditionalEmails: string | null;
    secondaryRecipientParties: NumericIdPrimKeyDto[];
    secondaryRecipientAdditionalEmails: string | null;
    matterClientReference: string | null;
    invoiceMatterName: string | null;
    invoiceMatterNameLocalLanguage: string | null;
    invoiceTemplateId: number | null;
    invoicedByOfficeId: number | null;
    invoicingIntervalTypeCode: string | null;
    payerPartyId: number | null;
    /** Absent or null keeps the stored payer list; sent only while the shared payment feature is on. */
    payers?: MatterPayerWriteDto[] | null;
    invoiceSpecTemplateId: number | null;
    referencedPricelist: PricelistReference | null;
    // The flat rows of one list; null whenever the save carries the full set of lists, as the form's does.
    rates: HourlyRate[] | null;
    personalRates?: PersonalRateInput[] | null;
    // The list the flat rows address; null is the base list.
    pricelistId?: number | null;
    // The full set of the case's lists, on create and update; a stored dated list missing from it is deleted.
    pricelists?: MatterPricelistWriteDto[] | null;
    // Absent leaves the link as it is, null makes an own copy of the followed list, an id follows that list.
    followsPricelist?: { id: number } | null;
    // The full set of a following case's price exceptions; absent leaves them as they are.
    pricelistExceptions?: PersonalRateInput[];
    // Read on create only: a copy or a case opened from a won offer keeps its source's kind. The server ignores it
    // on an update.
    pricingKind?: PricingKind;
    // Read on create only: the case a copy is made from, an employee-priced source whose kind the copy may keep.
    copiedFromMatterId?: number | null;
    pricelistCurrencyTypeCode: string | null;
    paymentCurrencyTypeCode: string | null;
    isJointInvoiceDisabled: boolean;
    isAutomaticClosingExempt: boolean;
    matterBlockingPolicyTypeCode: string | null;
    isAmlBlockingExempt: boolean;
    customFields?: CustomFieldDto[];
    nextInvoicingDate: Date | null;
    paymentNotificationReceivers: NamedEntityMinDto[];
    dmsLink: string | null;
    isAccessibleFirmWide: boolean;
    remunerationCalcTypeCode: string;
    isExcludedFromXR: boolean;
    preserveNonBillableHours: boolean;
    isPaperFile: boolean;
    invoiceCustomization?: CustomizationDto;
    isSpecIncludedInInvoice: boolean;
    isSpecExcludedFromEmail: boolean;
    entityFieldChangeReasons: CreateEntityFieldChangeReasonDto[] | null;
}

export interface MatterUpdateDto extends MatterCreateDto {
    id: string | number;
}

export interface MatterMin extends MinDto {
    fullName: string;
    caseNumber: string;
    isConfidential?: boolean | null;
}

export interface MatterMinWithClientParty extends MatterMin {
    clientParty: ClientParty;
}

export type MinDtoWithSuffix = MinDto & {
    /** Rendered before the link — where the case indicators go, since an icon leads what it describes. */
    prefix?: ReactNode;
    suffix?: ReactNode;
};

/** The kind of top-level group the case list's NEW tab counts the period's cases per. */
export enum NewCasesGroupBy {
    PRACTICE_GROUP = 'PRACTICE_GROUP',
    SECTOR_GROUP = 'SECTOR_GROUP',
}

/** One group's share of the period's cases; `id` and `name` are absent for the cases in no group. */
export interface NewCasesGroup {
    id?: number | null;
    name?: string | null;
    caseCount: number;
    offerCount: number;
}

export interface NewCasesSummary {
    caseCount: number;
    offerCount: number;
    groups: NewCasesGroup[];
    unassigned: NewCasesGroup;
}

/** The request both NEW-tab reads share; `officeIds` is comma-joined, empty meaning every office. */
export interface NewCasesParams extends Record<string, string> {
    groupBy: NewCasesGroupBy;
    officeIds: string;
    from: string;
    until: string;
}

export interface NewCaseListItem extends MatterVisibilityInfo {
    id: number;
    matterStateTypeCode: string;
    caseNumber: string;
    name: string;
    fullName: string;
    description?: string | null;
    openedDate: string;
    clientParty: ClientParty;
    supervisedByEmployee?: NamedEntityMinDto | null;
    responsibleEmployee?: NamedEntityMinDto | null;
    referrals?: ReferralListItem[] | null;
    relationshipPartners?: NamedEntityMinDto[] | null;
    relationshipManagers?: NamedEntityMinDto[] | null;
    pricelistCurrencyTypeCode?: string | null;
    minHourlyPrice?: number | null;
    maxHourlyPrice?: number | null;
    avgHourlyPrice?: number | null;
    otherGroups: string[];
}
